import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Wrench, ArrowRight, Phone, Calendar, Check, Shield, Clock, Award, Users, DollarSign, Settings, MapPin, HelpCircle, AlertTriangle } from 'lucide-react';
import { serviceAreas, installationServiceContent, installationSubServices } from '../../data/services/installationData';
import { localBusinessSchema, generateFAQSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/schemaMarkup';
import { ServiceVideoSection } from '../../components/services/ServiceVideoSection';
import { useSchemaMarkup } from '../../utils/useSchemaMarkup';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { PhoneLinkText } from '../../components/PhoneLinkText';

const InstallationPage = () => {
  const { subservice } = useParams();
  const current = subservice ? installationServiceContent[subservice] || installationServiceContent.main : installationServiceContent.main;
  const isMain = !subservice;

  // Generate schema markup
  const faqSchema = current.faqs ? generateFAQSchema(current.faqs) : null;
  const serviceSchema = generateServiceSchema(
    current.title,
    current.description,
    `https://comfortmasterheating.com/services/installation${subservice ? '/' + subservice : ''}`
  );
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Services', url: 'https://comfortmasterheating.com/services' },
    { name: 'Installation', url: 'https://comfortmasterheating.com/services/installation' },
    ...(subservice ? [{ name: current.title, url: `https://comfortmasterheating.com/services/installation/${subservice}` }] : [])
  ]);

  // Inject schemas into document head (bypasses react-helmet-async script issues)
  useSchemaMarkup(
    [localBusinessSchema, serviceSchema, breadcrumbSchema, faqSchema],
    `installation-${subservice || 'main'}`
  );

  return (
    <main>
      <Helmet>
        <title>{current.metaTitle}</title>
        <meta name="description" content={current.metaDescription} />
        <link rel="canonical" href={`https://comfortmasterheating.com/services/installation${subservice ? '/' + subservice : ''}`} />
      </Helmet>

      <section className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: 'Services', href: '/services' },
              { label: 'Installation', href: '/services/installation' },
              ...(subservice ? [{ label: current.title }] : [])
            ]}
            className="mb-4 text-emerald-200"
            linkClassName="hover:text-white transition-colors"
            separatorClassName="mx-1 opacity-60"
            currentClassName="text-white"
          />
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl">{current.title}</h1>
          </div>
          <p className="text-xl text-emerald-100 max-w-3xl mb-4">{current.description}</p>
          {current.extendedDescription && <p className="text-lg text-emerald-100/80 max-w-3xl mb-8">{current.extendedDescription}</p>}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary bg-white text-emerald-700 hover:bg-emerald-50" data-testid="installation-estimate-btn">
              <Calendar size={20} className="inline mr-2" />Request Free Estimate
            </Link>
            <a href="tel:8593576803" className="btn-hero-secondary inline-flex items-center justify-center gap-2 hover:text-emerald-700" data-testid="installation-call-btn">
              <Phone size={20} className="mr-2" />(859) 357-6803
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMain ? (
            <>
              <div className="text-center mb-12">
                <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">Our Installation Services</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">From new construction to system upgrades, our NATE-certified technicians install HVAC systems that deliver lasting comfort throughout Lexington, Nicholasville, and Central Kentucky.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                {installationSubServices.map((service, index) => (
                  <Link key={service.slug} to={`/services/installation/${service.slug}`} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group" data-testid={`installation-sub-${index}`}>
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-600 transition-colors">
                      <Settings className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{service.desc}</p>
                    <span className="inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm">Learn More <ArrowRight size={16} /></span>
                  </Link>
                ))}
              </div>

              {current.signs && (
                <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <AlertTriangle className="w-8 h-8 text-emerald-600" />
                    <h2 className="font-heading font-bold text-2xl text-slate-900">When You Need HVAC Installation</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {current.signs.map((sign, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={14} className="text-emerald-600" />
                        </div>
                        <span className="text-slate-700">{sign}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-8 text-center">Why Central Kentucky Chooses ComfortMaster</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { icon: Award, title: 'NATE Certified', desc: 'Expert installers with industry-leading credentials' },
                    { icon: Clock, title: 'Proper Sizing', desc: 'Manual J load calculations for every job' },
                    { icon: Users, title: '25+ Years Local', desc: 'Serving Lexington & Central KY since 1999' },
                    { icon: DollarSign, title: 'Financing Available', desc: 'Flexible payment options' }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-7 h-7 text-emerald-600" />
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {current.faqs && (
                <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                  <div className="flex items-center gap-3 mb-8">
                    <HelpCircle className="w-8 h-8 text-emerald-600" />
                    <h2 className="font-heading font-bold text-2xl text-slate-900">Frequently Asked Questions</h2>
                  </div>
                  <div className="space-y-6">
                    {current.faqs.map((faq, index) => (
                      <div key={index} className="border-b border-stone-200 pb-6 last:border-0">
                        <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                        <p className="text-slate-600"><PhoneLinkText text={faq.a} /></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Enhanced Service Areas with City Links */}
              <div className="bg-slate-900 rounded-2xl p-8 md:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-8 h-8 text-emerald-400" />
                  <h2 className="font-heading font-bold text-2xl text-white">Installation Service Areas</h2>
                </div>
                <p className="text-stone-300 mb-6">ComfortMaster provides HVAC installation services throughout Central Kentucky. Click on your city for local information:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
                  {serviceAreas.map((area) => (
                    <Link key={area.slug} to={`/service-areas/${area.slug}`} className="bg-slate-800 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm text-center transition-colors">
                      {area.name}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-white font-semibold mb-3">Popular Installation Services by City:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <Link to="/service-areas/lexington" className="text-emerald-400 hover:text-emerald-300">HVAC Installation Lexington KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/nicholasville" className="text-emerald-400 hover:text-emerald-300">New System Installation Nicholasville KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/georgetown" className="text-emerald-400 hover:text-emerald-300">Ductwork Installation Georgetown KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/richmond" className="text-emerald-400 hover:text-emerald-300">Smart Thermostat Richmond KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/frankfort" className="text-emerald-400 hover:text-emerald-300">HVAC Replacement Frankfort KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/versailles" className="text-emerald-400 hover:text-emerald-300">Zoning Systems Versailles KY</Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white p-8 rounded-2xl shadow-sm">
                  <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">About {current.title}</h2>
                  <p className="text-slate-700 mb-4">{current.description}</p>
                  {current.extendedDescription && <p className="text-slate-600">{current.extendedDescription}</p>}
                </div>

                {current.signs && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <AlertTriangle className="w-6 h-6 text-emerald-600" />
                      <h2 className="font-heading font-bold text-2xl text-slate-900">When You Need {current.title}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {current.signs.map((sign, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={14} className="text-emerald-600" />
                          </div>
                          <span className="text-slate-700 text-sm">{sign}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(current.process || current.services || current.benefits) && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">
                      {current.process ? 'Our Installation Process' : current.benefits ? 'Benefits' : 'Our Services'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(current.process || current.services || current.benefits || []).map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={14} className="text-emerald-600" />
                          </div>
                          <span className="text-slate-700 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {current.faqs && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <HelpCircle className="w-6 h-6 text-emerald-600" />
                      <h2 className="font-heading font-bold text-2xl text-slate-900">Common Questions</h2>
                    </div>
                    <div className="space-y-6">
                      {current.faqs.map((faq, index) => (
                        <div key={index} className="border-b border-stone-200 pb-6 last:border-0">
                          <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                          <p className="text-slate-600 text-sm"><PhoneLinkText text={faq.a} /></p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* City-specific service links for sub-pages */}
                <div className="bg-slate-100 p-6 rounded-2xl">
                  <h3 className="font-bold text-slate-900 mb-4">{current.title} Available In:</h3>
                  <div className="flex flex-wrap gap-2">
                    {serviceAreas.slice(0, 8).map((area) => (
                      <Link key={area.slug} to={`/service-areas/${area.slug}`} className="text-sm bg-white px-3 py-1 rounded-full text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors">
                        {area.name}, KY
                      </Link>
                    ))}
                    <Link to="/service-areas" className="text-sm bg-emerald-600 px-3 py-1 rounded-full text-white hover:bg-emerald-700 transition-colors">
                      + More Cities
                    </Link>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-2xl shadow-lg sticky top-24">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-6">Request an Estimate</h3>
                  <div className="space-y-4 mb-6">
                    {[
                      { icon: Shield, title: 'Licensed & Insured', desc: 'Fully licensed Kentucky technicians' },
                      { icon: Clock, title: 'Free Estimates', desc: 'No-obligation consultations' },
                      { icon: Award, title: 'NATE Certified', desc: 'Industry-leading certification' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                          <p className="text-slate-600 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className="btn-primary w-full text-center block mb-3" data-testid="installation-cta-btn">Request Estimate</Link>
                  <a href="tel:8593576803" className="btn-secondary w-full text-center block text-sm">
                    <Phone size={16} className="inline mr-2" />(859) 357-6803
                  </a>
                  <div className="mt-8 pt-6 border-t border-stone-200">
                    <h4 className="font-bold text-slate-900 mb-4 text-sm">Other Installation Services</h4>
                    <div className="space-y-2">
                      {installationSubServices.filter(s => s.slug !== subservice).map(s => (
                        <Link key={s.slug} to={`/services/installation/${s.slug}`} className="block text-sm text-slate-600 hover:text-emerald-600">→ {s.title}</Link>
                      ))}
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-stone-200">
                    <h4 className="font-bold text-slate-900 mb-4 text-sm">Service Areas</h4>
                    <p className="text-slate-600 text-xs mb-3">Serving Lexington, Nicholasville, Georgetown, and all of Central Kentucky.</p>
                    <Link to="/service-areas" className="text-emerald-600 text-sm font-semibold hover:text-emerald-700">View All Areas →</Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <ServiceVideoSection />

      <section className="py-16 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">Ready to Plan Your HVAC Project?</h2>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">Contact ComfortMaster today for expert installation services in Lexington, Nicholasville, and throughout Central Kentucky.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-white text-emerald-700 hover:bg-emerald-50">Request Estimate</Link>
            <Link to="/specials" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-emerald-700">View Specials</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default InstallationPage;
